import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Select,
  Alert,
  AlertIcon,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Container,
  Heading,
  Divider,
  FormControl
} from '@chakra-ui/react';
import { 
  ContentModerationService, 
  type ContentReport, 
  type UserReport, 
  type DMCARequest, 
  type UserViolation,
  type ModerationStats 
} from '../services/moderationService';
import { useAuth } from '../contexts/AuthContext';

const AdminModeration: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states
  const [contentReports, setContentReports] = useState<ContentReport[]>([]);
  const [userReports, setUserReports] = useState<UserReport[]>([]);
  const [dmcaRequests, setDmcaRequests] = useState<DMCARequest[]>([]);
  const [userViolations, setUserViolations] = useState<UserViolation[]>([]);
  const [stats, setStats] = useState<ModerationStats | null>(null);
  
  // Modal states
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [selectedUserReport, setSelectedUserReport] = useState<UserReport | null>(null);
  const [selectedDMCA, setSelectedDMCA] = useState<DMCARequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      const [contentReportsData, userReportsData, dmcaData, violationsData, statsData] = await Promise.all([
        ContentModerationService.getAllContentReports(),
        ContentModerationService.getAllUserReports(),
        ContentModerationService.getAllDMCARequests(),
        ContentModerationService.getUserViolations(''), // Get all violations
        ContentModerationService.getModerationStats()
      ]);
      
      setContentReports(contentReportsData);
      setUserReports(userReportsData);
      setDmcaRequests(dmcaData);
      setUserViolations(violationsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading moderation data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load moderation data. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateReportStatus = async (reportId: string, status: string) => {
    try {
      await ContentModerationService.updateReportStatus(
        reportId,
        status as any,
        adminNotes || undefined,
        user?.uid
      );
      
      toast({
        title: 'Report updated',
        description: 'Report status has been updated successfully.',
        status: 'success'
      });
      
      setIsModalOpen(false);
      setSelectedReport(null);
      setAdminNotes('');
      setNewStatus('');
      loadAllData();
    } catch (error) {
      console.error('Error updating report:', error);
      toast({
        title: 'Error updating report',
        description: 'Failed to update report status.',
        status: 'error'
      });
    }
  };

  const handleIssueViolation = async (userId: string, userName: string, reportId: string) => {
    try {
      await ContentModerationService.issueUserViolation(
        userId,
        userName,
        'content_violation',
        'Violation based on content report',
        'warning',
        undefined,
        reportId,
        adminNotes || undefined,
        user?.uid || ''
      );
      
      toast({
        title: 'Violation issued',
        description: 'User violation has been issued successfully.',
        status: 'success'
      });
      
      setIsModalOpen(false);
      setSelectedReport(null);
      setAdminNotes('');
      loadAllData();
    } catch (error) {
      console.error('Error issuing violation:', error);
      toast({
        title: 'Error issuing violation',
        description: 'Failed to issue user violation.',
        status: 'error'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'reviewing': return 'blue';
      case 'resolved': return 'green';
      case 'dismissed': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  if (!user) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Please log in to access the moderation panel.</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={8} align="stretch">
        <VStack gap={2} textAlign="center">
          <Heading size="lg" color="white">Content Moderation Panel</Heading>
          <Text color="gray.400">Manage reports, violations, and DMCA requests</Text>
        </VStack>

        {/* Stats Overview */}
        {stats && (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
            <Stat bg="gray.800" p={4} borderRadius="md">
              <StatLabel color="gray.400">Total Reports</StatLabel>
              <StatNumber color="white">{stats.totalReports}</StatNumber>
              <StatHelpText color="gray.500">{stats.pendingReports} pending</StatHelpText>
            </Stat>
            <Stat bg="gray.800" p={4} borderRadius="md">
              <StatLabel color="gray.400">DMCA Requests</StatLabel>
              <StatNumber color="white">{stats.totalDMCARequests}</StatNumber>
              <StatHelpText color="gray.500">{stats.pendingDMCARequests} pending</StatHelpText>
            </Stat>
            <Stat bg="gray.800" p={4} borderRadius="md">
              <StatLabel color="gray.400">Violations</StatLabel>
              <StatNumber color="white">{stats.totalViolations}</StatNumber>
              <StatHelpText color="gray.500">{stats.activeViolations} active</StatHelpText>
            </Stat>
            <Stat bg="gray.800" p={4} borderRadius="md">
              <StatLabel color="gray.400">Banned Users</StatLabel>
              <StatNumber color="red.400">{stats.bannedUsers}</StatNumber>
              <StatHelpText color="gray.500">{stats.suspendedUsers} suspended</StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        <Divider />

        {/* Tabs */}
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
          <TabList>
            <Tab>Content Reports</Tab>
            <Tab>User Reports</Tab>
            <Tab>DMCA Requests</Tab>
            <Tab>User Violations</Tab>
          </TabList>

          <TabPanels>
            {/* Content Reports Tab */}
            <TabPanel>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="lg" fontWeight="bold" color="white">
                    Content Reports ({contentReports.length})
                  </Text>
                  <Button onClick={loadAllData} size="sm">
                    Refresh
                  </Button>
                </HStack>

                <Box overflowX="auto">
                  <Table variant="simple" colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th color="gray.300">Content</Th>
                        <Th color="gray.300">Type</Th>
                        <Th color="gray.300">Reporter</Th>
                        <Th color="gray.300">Priority</Th>
                        <Th color="gray.300">Status</Th>
                        <Th color="gray.300">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {contentReports.map((report) => (
                        <Tr key={report.id}>
                          <Td>
                            <VStack align="start" gap={1}>
                              <Text fontWeight="bold" color="white">{report.contentTitle}</Text>
                              <Text fontSize="sm" color="gray.400">by {report.contentCreatorName}</Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">{report.reportType}</Badge>
                          </Td>
                          <Td color="gray.300">{report.reporterName}</Td>
                          <Td>
                            <Badge colorScheme={getPriorityColor(report.priority)}>
                              {report.priority}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(report.status)}>
                              {report.status}
                            </Badge>
                          </Td>
                          <Td>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedReport(report);
                                setIsModalOpen(true);
                              }}
                            >
                              Review
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            {/* User Reports Tab */}
            <TabPanel>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="lg" fontWeight="bold" color="white">
                    User Reports ({userReports.length})
                  </Text>
                  <Button onClick={loadAllData} size="sm">
                    Refresh
                  </Button>
                </HStack>

                <Box overflowX="auto">
                  <Table variant="simple" colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th color="gray.300">Reported User</Th>
                        <Th color="gray.300">Type</Th>
                        <Th color="gray.300">Reporter</Th>
                        <Th color="gray.300">Priority</Th>
                        <Th color="gray.300">Status</Th>
                        <Th color="gray.300">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {userReports.map((report) => (
                        <Tr key={report.id}>
                          <Td color="white" fontWeight="bold">{report.reportedUserName}</Td>
                          <Td>
                            <Badge colorScheme="blue">{report.reportType}</Badge>
                          </Td>
                          <Td color="gray.300">{report.reporterName}</Td>
                          <Td>
                            <Badge colorScheme={getPriorityColor(report.priority)}>
                              {report.priority}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(report.status)}>
                              {report.status}
                            </Badge>
                          </Td>
                          <Td>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedUserReport(report);
                                setIsModalOpen(true);
                              }}
                            >
                              Review
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            {/* DMCA Requests Tab */}
            <TabPanel>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="lg" fontWeight="bold" color="white">
                    DMCA Requests ({dmcaRequests.length})
                  </Text>
                  <Button onClick={loadAllData} size="sm">
                    Refresh
                  </Button>
                </HStack>

                <Box overflowX="auto">
                  <Table variant="simple" colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th color="gray.300">Content</Th>
                        <Th color="gray.300">Claimant</Th>
                        <Th color="gray.300">Copyright Work</Th>
                        <Th color="gray.300">Status</Th>
                        <Th color="gray.300">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dmcaRequests.map((request) => (
                        <Tr key={request.id}>
                          <Td>
                            <VStack align="start" gap={1}>
                              <Text fontWeight="bold" color="white">{request.contentTitle}</Text>
                              <Text fontSize="sm" color="gray.400">by {request.contentCreatorName}</Text>
                            </VStack>
                          </Td>
                          <Td color="gray.300">{request.claimantName}</Td>
                          <Td color="gray.300">{request.copyrightWork}</Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(request.status)}>
                              {request.status}
                            </Badge>
                          </Td>
                          <Td>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedDMCA(request);
                                setIsModalOpen(true);
                              }}
                            >
                              Review
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            {/* User Violations Tab */}
            <TabPanel>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="lg" fontWeight="bold" color="white">
                    User Violations ({userViolations.length})
                  </Text>
                  <Button onClick={loadAllData} size="sm">
                    Refresh
                  </Button>
                </HStack>

                <Box overflowX="auto">
                  <Table variant="simple" colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th color="gray.300">User</Th>
                        <Th color="gray.300">Violation Type</Th>
                        <Th color="gray.300">Severity</Th>
                        <Th color="gray.300">Status</Th>
                        <Th color="gray.300">Issued By</Th>
                        <Th color="gray.300">Date</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {userViolations.map((violation) => (
                        <Tr key={violation.id}>
                          <Td color="white" fontWeight="bold">{violation.userName}</Td>
                          <Td>
                            <Badge colorScheme="blue">{violation.violationType}</Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getPriorityColor(violation.severity)}>
                              {violation.severity}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(violation.status)}>
                              {violation.status}
                            </Badge>
                          </Td>
                          <Td color="gray.300">{violation.issuedBy}</Td>
                          <Td color="gray.300">
                            {violation.issuedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Review Modal */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>
              {selectedReport && 'Review Content Report'}
              {selectedUserReport && 'Review User Report'}
              {selectedDMCA && 'Review DMCA Request'}
            </ModalHeader>
            <ModalBody>
              <VStack gap={4} align="stretch">
                {selectedReport && (
                  <>
                    <Text fontWeight="bold">Content: {selectedReport.contentTitle}</Text>
                    <Text>Type: {selectedReport.reportType}</Text>
                    <Text>Reporter: {selectedReport.reporterName}</Text>
                    <Text>Description: {selectedReport.description}</Text>
                    {selectedReport.evidence && selectedReport.evidence.length > 0 && (
                      <Text>Evidence: {selectedReport.evidence.join(', ')}</Text>
                    )}
                  </>
                )}
                
                {selectedUserReport && (
                  <>
                    <Text fontWeight="bold">Reported User: {selectedUserReport.reportedUserName}</Text>
                    <Text>Type: {selectedUserReport.reportType}</Text>
                    <Text>Reporter: {selectedUserReport.reporterName}</Text>
                    <Text>Description: {selectedUserReport.description}</Text>
                  </>
                )}
                
                {selectedDMCA && (
                  <>
                    <Text fontWeight="bold">Content: {selectedDMCA.contentTitle}</Text>
                    <Text>Claimant: {selectedDMCA.claimantName}</Text>
                    <Text>Copyright Work: {selectedDMCA.copyrightWork}</Text>
                    <Text>Description: {selectedDMCA.description}</Text>
                  </>
                )}

                <FormControl>
                  <Textarea
                    placeholder="Admin notes..."
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <Select
                    placeholder="Select new status"
                    bg="gray.700"
                    border="none"
                    color="white"
                    _focus={{ bg: 'gray.600' }}
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              {selectedReport && (
                <>
                  <Button
                    colorScheme="red"
                    mr={2}
                    onClick={() => handleIssueViolation(
                      selectedReport.contentCreatorId,
                      selectedReport.contentCreatorName,
                      selectedReport.id
                    )}
                  >
                    Issue Violation
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={() => handleUpdateReportStatus(selectedReport.id, newStatus)}
                    isDisabled={!newStatus}
                  >
                    Update Status
                  </Button>
                </>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default AdminModeration;
